import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createCheckoutSession,
  createCoupon,
  createPortalSession,
  fetchCoupons,
  fetchPlans,
  fetchSubscription,
  fetchUsage,
  fetchUsageDayDetail,
  fetchUsageHistory,
} from "./billing.api";
import type {
  CheckoutSessionPayload,
  CreateCouponPayload,
} from "./billing.types";

const KEYS = {
  plans: ["billing", "plans"] as const,
  subscription: ["billing", "subscription"] as const,
  usage: ["billing", "usage"] as const,
  usageHistory: (days: number) => ["billing", "usage-history", days] as const,
  coupons: ["billing", "coupons"] as const,
};

export const useBillingPlans = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: KEYS.plans,
    queryFn: fetchPlans,
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });

export const useSubscription = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: KEYS.subscription,
    queryFn: fetchSubscription,
    staleTime: 60 * 1000,
    enabled: options?.enabled ?? true,
    // Don't blow up the page if user isn't allowed yet — pages handle their own 403 UI.
    retry: (failureCount, err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 1;
    },
  });

export const useUsage = (options?: {
  enabled?: boolean;
  refetchIntervalMs?: number;
}) =>
  useQuery({
    queryKey: KEYS.usage,
    queryFn: fetchUsage,
    staleTime: 30 * 1000,
    refetchInterval: options?.refetchIntervalMs ?? false,
    enabled: options?.enabled ?? true,
    retry: (failureCount, err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403 || status === 404) return false;
      return failureCount < 1;
    },
  });

export const useCreateCheckoutSession = () =>
  useMutation({
    mutationFn: (payload: CheckoutSessionPayload) =>
      createCheckoutSession(payload),
  });

export const useCreatePortalSession = () =>
  useMutation({
    mutationFn: () => createPortalSession(),
  });

export const useCoupons = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: KEYS.coupons,
    queryFn: fetchCoupons,
    enabled: options?.enabled ?? true,
    retry: (failureCount, err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 1;
    },
  });

export const useCreateCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCouponPayload) => createCoupon(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.coupons });
    },
  });
};

export const useUsageHistory = (days = 30, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: KEYS.usageHistory(days),
    queryFn: () => fetchUsageHistory(days),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
    retry: (failureCount, err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403 || status === 404) return false;
      return failureCount < 1;
    },
  });

export const useUsageDayDetail = (
  date: string | null,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ["billing", "usage-day-detail", date],
    queryFn: () => fetchUsageDayDetail(date!),
    staleTime: 5 * 60 * 1000,
    enabled: (options?.enabled ?? true) && !!date,
    retry: false,
  });
